import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { quotationsAPI, suppliersAPI } from '@/lib/api-client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Loader2, Search, Users } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  quotationId: string
  existingSupplierIds: string[]
}

export function AddSupplierDialog({ open, onOpenChange, quotationId, existingSupplierIds }: Props) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<any>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['suppliers-list-qc', search],
    queryFn: () => suppliersAPI.list({ search: search || undefined, limit: 50, isActive: true }),
    enabled: open,
  })

  const rawList = data?.data || data || []
  const suppliers = Array.isArray(rawList)
    ? rawList.filter((s: any) => !existingSupplierIds.includes(s.id))
    : []

  const mutation = useMutation({
    mutationFn: () =>
      quotationsAPI.addSupplier(quotationId, { supplierId: selected.id }),
    onSuccess: () => {
      toast.success(`${selected.name} adicionado ao QC.`)
      queryClient.invalidateQueries({ queryKey: ['quotation', quotationId] })
      handleClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const handleClose = () => {
    setSelected(null)
    setSearch('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Adicionar Fornecedor</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar fornecedor..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              autoFocus
            />
          </div>

          <div className="max-h-72 overflow-y-auto rounded-md border divide-y">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : suppliers.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-8 text-neutral-500">
                <Users className="h-8 w-8 text-neutral-300" />
                <p className="text-sm">
                  {search ? 'Nenhum fornecedor encontrado' : 'Nenhum fornecedor cadastrado'}
                </p>
              </div>
            ) : (
              suppliers.map((s: any) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSelected(s)}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-neutral-50 transition-colors',
                    selected?.id === s.id && 'bg-primary/5 ring-1 ring-inset ring-primary/20',
                  )}
                >
                  <div>
                    <p className="text-sm font-medium">{s.name}</p>
                    {s.document && (
                      <p className="text-xs text-neutral-400">{s.document}</p>
                    )}
                  </div>
                  {s.type && (
                    <span className="ml-4 shrink-0 text-xs text-neutral-400">{s.type}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            disabled={!selected || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
